import { Box, Group, Text } from "@mantine/core"
import { IconMinus, IconPlus, IconZoomOut } from "@tabler/icons-react"
import { type ReactNode, useEffect, useRef, useState } from "react"

import { ActionIcon } from "../../atoms/ActionIcons/ActionIcon"

import classes from "../ScrollArea/ScrollArea.module.css"

interface ZoomContainerProps {
  children: ReactNode
  zoom: number
}

type NaturalSize = {
  width: number
  height: number
}

/**
 * Scales content. Must not create its own scroller — the parent canvas owns overflow.
 * Outer box layout size tracks `naturalSize * zoom` so scroll metrics match the
 * visually scaled content (avoids false edge fades / steps when zoomed out).
 */
export const ZoomContainer = ({ children, zoom }: ZoomContainerProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [naturalSize, setNaturalSize] = useState<NaturalSize>({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    const content = contentRef.current
    if (!content) return

    const updateNaturalSize = () => {
      setNaturalSize({
        width: content.offsetWidth,
        height: content.offsetHeight,
      })
    }

    updateNaturalSize()
    const resizeObserver = new ResizeObserver(updateNaturalSize)
    resizeObserver.observe(content)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  const hasMeasuredSize = naturalSize.width > 0 && naturalSize.height > 0

  return (
    <Box
      className={classes.zoomContainer}
      style={
        hasMeasuredSize
          ? {
              width: naturalSize.width * zoom,
              height: naturalSize.height * zoom,
            }
          : undefined
      }
    >
      <Box
        ref={contentRef}
        className={classes.zoomedContent}
        style={{
          transform: `scale(${zoom})`,
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

interface ZoomControlsProps {
  zoom: number
  zoomIndex: number
  onZoomIn: () => void
  onZoomOut: () => void
}

/** Sibling of the canvas so controls stay pinned while content pans. */
export const ZoomControls = ({
  zoom,
  zoomIndex,
  onZoomIn,
  onZoomOut,
}: ZoomControlsProps) => {
  return (
    <Group gap={2} className={classes.zoomToggle}>
      {zoomIndex !== 0 ? (
        <>
          <ActionIcon
            aria-label="Zoom out"
            icon={<IconMinus />}
            size={16}
            variant="ghost"
            onClick={onZoomOut}
          />
          <ActionIcon
            aria-label="Zoom in"
            icon={<IconPlus />}
            size={16}
            variant="ghost"
            onClick={onZoomIn}
          />
          <Text size="xs" fw={500}>
            {Math.round(zoom * 100)}%
          </Text>
        </>
      ) : (
        <ActionIcon
          aria-label="Zoom out"
          icon={<IconZoomOut />}
          size={16}
          variant="ghost"
          onClick={onZoomOut}
        />
      )}
    </Group>
  )
}
